import _ from 'underscore';
import { useCallback } from 'react';
import {
  AssetEngagementEventName,
  AssetEventOperationName,
  AssetEventRecordType,
  POST_ASSET_EVENT_NAME,
} from './constants';
import { getSuggestedIdsAndAssetType, getSuggestedIdsAndAssetTypeFromAssetsRecommendation, getOperationName } from './utils';
import { prefillAssetsAppliedLog } from './event-logs';

/**
 * Event logging used to gather adoption metrics for the copilot asset suggestions.
 * @param {Function} postAssetEventLog - Logging function.
 * @returns {Object} - An object containing the event logging functions.
 */
export function useEventLog(postAssetEventLog) {
  const logEnterFlow = useCallback(() => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.ENTER_FLOW,
      },
    });
  }, [postAssetEventLog]);

  const logFinalUrlRecommendations = useCallback((FinalUrls) => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.RETURNED,
        OperationName: AssetEventOperationName.FINAL_URL_RECOMMENDATION,
        FinalUrls,
      },
    });
  }, [postAssetEventLog]);

  const logBannerGenerated = useCallback((CurrentFinalUrl, assetsRecommendationData) => {
    const { SuggestionIds, AssetType } = getSuggestedIdsAndAssetTypeFromAssetsRecommendation(assetsRecommendationData);

    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.RETURNED,
        OperationName: AssetEventOperationName.NON_ITERATIVE,
        SuggestionIds,
        AssetType,
        CurrentFinalUrl,
      },
    });
  }, [postAssetEventLog]);

  const logBannerRefresh = useCallback(() => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        OperationName: AssetEventOperationName.REFRESH_THEME,
        EngagementEventName: AssetEngagementEventName.ENTER,
      },
    });
  }, [postAssetEventLog]);

  const logBannerRefreshCompleted = useCallback(() => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        OperationName: AssetEventOperationName.REFRESH_THEME,
        EngagementEventName: AssetEngagementEventName.VIEW,
      },
    });
  }, [postAssetEventLog]);

  const logFetch = useCallback(() => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        OperationName: AssetEventOperationName.REFRESH_THEME,
        EngagementEventName: AssetEngagementEventName.GENERATE,
      },
    });
  }, [postAssetEventLog]);

  const logFetchSucceeded = useCallback((finalUrl, themes) => {
    const { SuggestionIds, AssetType } = getSuggestedIdsAndAssetType({ themeSuggestionIds: _.pluck(themes, 'RecommendationId') });

    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.RETURNED,
        OperationName: AssetEventOperationName.REFRESH_THEME,
        SuggestionIds,
        AssetType,
        CurrentFinalUrl: finalUrl,
      },
    });

    // assume all themes are viewed by default
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.VIEWED,
        OperationName: AssetEventOperationName.REFRESH_THEME,
        SuggestionIds,
        AssetType,
      },
    });
  }, [postAssetEventLog]);

  const logFetchAssetsSucceeded = useCallback((finalUrl, assetsRecommendationData) => {
    const { SuggestionIds, AssetType } = getSuggestedIdsAndAssetTypeFromAssetsRecommendation(assetsRecommendationData);

    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.RETURNED,
        OperationName: AssetEventOperationName.REFRESH_THEME,
        SuggestionIds,
        AssetType,
        CurrentFinalUrl: finalUrl,
      },
    });
  }, [postAssetEventLog]);

  const logApplyThemes = useCallback((themes) => {
    const { SuggestionIds, AssetType } = getSuggestedIdsAndAssetType({ themeSuggestionIds: _.pluck(themes, 'RecommendationId') });

    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        OperationName: AssetEventOperationName.REFRESH_THEME,
        EngagementEventName: AssetEngagementEventName.APPLY,
        SuggestionIds,
        AssetType,
      },
    });
  }, [postAssetEventLog]);

  const logSuggestionsViewed = useCallback((textSuggestionIds, imageSuggestionIds, videoSuggestionIds) => {
    const { SuggestionIds, AssetType } = getSuggestedIdsAndAssetType({ textSuggestionIds, imageSuggestionIds, videoSuggestionIds });

    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.VIEWED,
        SuggestionIds,
        AssetType,
      },
    });
  }, [postAssetEventLog]);

  const logSuggestionsApplied = useCallback((CampaignId, AssetGroupId, textSuggestionIds, imageSuggestionIds, videoSuggestionIds, appliedVideoAssetIds = []) => {
    const { SuggestionIds, AssetType } = getSuggestedIdsAndAssetType({ textSuggestionIds, imageSuggestionIds, videoSuggestionIds });

    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        CampaignId,
        AssetGroupId,
        RecordType: AssetEventRecordType.APPLIED,
        SuggestionIds,
        AssetType,
        ...(appliedVideoAssetIds.length > 0 && { AppliedVideoAssetIds: appliedVideoAssetIds }),
      },
    });
  }, [postAssetEventLog]);

  const logTextAssetMenuOpen = useCallback((TextAssetType) => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        OperationName: 'InlineCopilot',
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        EngagementEventName: AssetEngagementEventName.VIEW,
        TextAssetType,
      },
    });
  }, [postAssetEventLog]);

  const logTextAssetDialogOpen = useCallback((TextAssetType, OperationName) => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        EngagementEventName: AssetEngagementEventName.ENTER,
        OperationName,
        TextAssetType,
      },
    });
  }, [postAssetEventLog]);

  const logRefineTextAsset = useCallback((TextAssetType, requestType) => {
    const OperationName = getOperationName(requestType);

    if (OperationName) {
      postAssetEventLog({
        EventName: POST_ASSET_EVENT_NAME,
        EventValue: {
          RecordType: AssetEventRecordType.FEATURE_USAGE,
          EngagementEventName: AssetEngagementEventName.GENERATE,
          OperationName,
          TextAssetType,
        },
      });
    }
  }, [postAssetEventLog]);

  const logRefineTextAssetSucceeded = useCallback((finalUrl, params, textSuggestionIds) => {
    const OperationName = getOperationName(params.requestType);

    if (OperationName && textSuggestionIds) {
      const { SuggestionIds, AssetType } = getSuggestedIdsAndAssetType({ textSuggestionIds });

      postAssetEventLog({
        EventName: POST_ASSET_EVENT_NAME,
        EventValue: {
          RecordType: AssetEventRecordType.RETURNED,
          OperationName,
          SuggestionIds,
          AssetType,
          CurrentFinalUrl: finalUrl,
        },
      });

      // by default, the user views all recommendations returned
      postAssetEventLog({
        EventName: POST_ASSET_EVENT_NAME,
        EventValue: {
          RecordType: AssetEventRecordType.VIEWED,
          SuggestionIds,
          AssetType,
        },
      });
    }
  }, [postAssetEventLog]);

  const logTextAssetApplied = useCallback((TextAssetType, OperationName) => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        EngagementEventName: AssetEngagementEventName.APPLY,
        OperationName,
        TextAssetType,
      },
    });
  }, [postAssetEventLog]);

  const logPrefillAssets = useCallback(() => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        OperationName: AssetEventOperationName.PREFILL,
        EngagementEventName: AssetEngagementEventName.PREFILL,
      },
    });
  }, [postAssetEventLog]);

  const logPrefillAssetsApplied = useCallback((CampaignId, initialSuggestionsData) => {
    postAssetEventLog(prefillAssetsAppliedLog(CampaignId, initialSuggestionsData));
  }, [postAssetEventLog]);

  const logPrefilledAdStrength = useCallback((adStrength, initialSuggestionsData) => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        OperationName: AssetEventOperationName.PREFILL,
        EngagementEventName: AssetEngagementEventName.AD_STRENGTH,
        AdStrength: adStrength,
        Suggestions: initialSuggestionsData,
      },
    });
  }, [postAssetEventLog]);

  const logRemoveAssets = useCallback((PropertyName) => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        OperationName: AssetEventOperationName.PREFILL,
        EngagementEventName: AssetEngagementEventName.REMOVE_ASSETS,
        PropertyName,
      },
    });
  }, [postAssetEventLog]);

  const logRemoveSingleAsset = useCallback((PropertyName, SuggestionId) => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        OperationName: AssetEventOperationName.PREFILL,
        EngagementEventName: AssetEngagementEventName.REMOVE_SINGLE_ASSET,
        PropertyName,
        SuggestionId,
      },
    });
  }, [postAssetEventLog]);

  const logEditSingleAsset = useCallback((PropertyName, SuggestionId) => {
    postAssetEventLog({
      EventName: POST_ASSET_EVENT_NAME,
      EventValue: {
        RecordType: AssetEventRecordType.FEATURE_USAGE,
        OperationName: AssetEventOperationName.PREFILL,
        EngagementEventName: AssetEngagementEventName.EDIT_SINGLE_ASSET,
        PropertyName,
        SuggestionId,
      },
    });
  }, [postAssetEventLog]);

  return {
    logEnterFlow,
    logFinalUrlRecommendations,
    logBannerGenerated,
    logBannerRefresh,
    logBannerRefreshCompleted,
    logFetch,
    logFetchSucceeded,
    logFetchAssetsSucceeded,
    logApplyThemes,
    logSuggestionsViewed,
    logSuggestionsApplied,
    logTextAssetMenuOpen,
    logTextAssetDialogOpen,
    logRefineTextAsset,
    logRefineTextAssetSucceeded,
    logTextAssetApplied,
    logPrefillAssets,
    logPrefillAssetsApplied,
    logPrefilledAdStrength,
    logRemoveAssets,
    logRemoveSingleAsset,
    logEditSingleAsset,
  };
}

